/**
 * RESPONSIBILITY: which sections make up the events page, and in what order.
 *
 * Three of the four bands read the SAME Events section, so their pencils all open the
 * one list - which is the point. A special event is an event with `special` ticked, not
 * a different kind of thing kept somewhere else.
 *
 * `tag` narrows every band to one category, because `#events/men` is a URL that promises
 * it. Showing the whole list under that address would make the address a lie.
 */
import type { EventRow } from '@app/sections';
import { SectionName } from '@app/sections';
import { SectionUpdate } from '../cms';
import { PageHead, EventsFeatured, EventsAll, EventsSpecial, byNext, isVisible, slugify } from '../site';

export function EventsPage({ tag }: { tag?: string }) {
  // The URL carries a slug and the row carries the words, so they meet in the middle
  // rather than the tag having to be URL-shaped in the editor.
  const tagged = (e: EventRow) =>
    !tag || (e.categories ?? []).some((c) => slugify(c) === tag);

  return (
    <main>
      <SectionUpdate name={SectionName.EventsHero}>
        {(c) => <PageHead eyebrow={c.eyebrow} heading={c.heading} intro={c.intro} />}
      </SectionUpdate>

      {/* ONE filter, everywhere: isVisible answers the whole question — see when.ts. The
          switch used to be applied here and only here, so an event turned off was still
          in the featured strip and the special band, which is two answers to "is this
          shown" and no way to tell which one was being obeyed.

          `canEdit` widens it rather than replacing it: an editor is shown the same list a
          visitor gets PLUS the ones a visitor cannot see, each marked — so what is
          missing from the public page is visible where somebody would look for it. */}
      <SectionUpdate name={SectionName.Events}>
        {(c, edit) => (
          <EventsFeatured
            c={c}
            events={(c.events ?? [])
              .filter((e) => (edit.canEdit || isVisible(e)) && e.featured && tagged(e))
              .sort(byNext)}
          />
        )}
      </SectionUpdate>

      <SectionUpdate name={SectionName.Events}>
        {(c, edit) => (
          <EventsAll
            c={c}
            events={(c.events ?? [])
              .filter((e) => (edit.canEdit || isVisible(e)) && tagged(e))
              .sort(byNext)}
          />
        )}
      </SectionUpdate>

      <SectionUpdate name={SectionName.Events}>
        {(c, edit) => (
          <EventsSpecial
            c={c}
            events={(c.events ?? [])
              .filter((e) => (edit.canEdit || isVisible(e)) && e.special && tagged(e))}
          />
        )}
      </SectionUpdate>
    </main>
  );
}
